import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { DateField, PageHeader } from '@routes-plan/ui';
import { StandardModalBody, StandardModalFooter } from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import FormActions from '../shared/FormActions';
import LocationPicker, { isLocationComplete, toApiLocation } from '../shared/LocationPicker';
import {
  BatchRow,
  INVENTORY_ROUTES,
  OfficeOption,
  STOCK_STATUS_LABEL,
  ShowAlerts,
  StockRow,
  StockStatus,
  apiErrorMessage,
  fetchOffices,
  fetchStock,
  formatDate,
  newRequestId,
} from '../shared/inventoryApi';
import { ActionIcon, FieldGrid, IntField, PageBody, SaveButton, Section, useInventoryAlert } from '../shared/ui';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

type Loc = { officeId: number | null; routeId: number | null };
type Line = { key: number; productId: number | ''; status: StockStatus; batchId: number | ''; quantity: string };
const emptyLine = (key: number): Line => ({ key, productId: '', status: 'DISPONIBLE', batchId: '', quantity: '' });

/** Traslado entre una sede y sus rutas (cargue de ruta y devoluciones). */
interface TransferAddProps extends ShowAlerts {
  /** 'modal': dentro de StandardModal (composed), desde el listado de traslados. */
  variant?: 'page' | 'modal';
  onClose?: () => void;
  onSaved?: () => void;
}

const StockTransferNew: React.FC<TransferAddProps> = ({ showLoading, hideLoading, variant = 'page', onClose, onSaved }) => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const navigate = useNavigate();
  const inModal = variant === 'modal';
  const { canTransfer } = useResourcePermissions(INVENTORY_ROUTES.transfers);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [origin, setOrigin] = useState<Loc>({ officeId: null, routeId: null });
  const [destination, setDestination] = useState<Loc>({ officeId: null, routeId: null });
  const [stock, setStock] = useState<StockRow[]>([]);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [date, setDate] = useState(nowOperationalDateString());
  const [observation, setObservation] = useState('');
  const [lines, setLines] = useState<Line[]>([emptyLine(1)]);
  const [errors, setErrors] = useState<{ location?: string; lines?: string }>({});
  const [saving, setSaving] = useState(false);
  const requestId = useRef(newRequestId('transfer'));
  if (!inModal) setPageTitle('inventory.transfers.newPageTitle');

  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);

  // El destino siempre queda en la misma oficina del origen.
  useEffect(() => {
    setDestination({ officeId: origin.officeId, routeId: null });
  }, [origin.officeId]);

  useEffect(() => {
    if (!isLocationComplete(origin)) {
      setStock([]);
      setBatches([]);
      return;
    }
    fetchStock(toApiLocation(origin), true)
      .then(({ data, batches: b }) => {
        setStock(data);
        setBatches(b);
      })
      .catch((error) => showAlert(apiErrorMessage(error, t('inventory.transfers.originStockError')), 'error'));
    setLines([emptyLine(1)]);
  }, [origin, showAlert, t]);

  const stockById = useMemo(() => new Map(stock.map((s) => [s.productId, s])), [stock]);
  const products = stock.filter((s) => s.available > 0 || s.notLendable > 0);
  const updateLine = (key: number, patch: Partial<Line>) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const sameAsOrigin =
    Number(origin.routeId || 0) === Number(destination.routeId || 0) && Number(origin.officeId || 0) === Number(destination.officeId || 0);

  const save = async () => {
    if (!canTransfer) return;
    const nextErrors: { location?: string; lines?: string } = {};
    if (!isLocationComplete(origin) || !isLocationComplete(destination) || sameAsOrigin) nextErrors.location = t('inventory.transfers.sameLocation');
    const items = lines.filter((l) => l.productId !== '');
    if (!items.length || items.some((l) => !(Number(l.quantity) > 0))) nextErrors.lines = t('inventory.common.addLineWarning');
    setErrors(nextErrors);
    if (nextErrors.location || nextErrors.lines) return;
    setSaving(true);
    showLoading();
    try {
      const { data: saved } = await axios.post('stock-transfers', {
        date,
        origin: toApiLocation(origin),
        destination: toApiLocation(destination),
        observation: observation || null,
        clientRequestId: requestId.current,
        items: items.map((l) => ({
          productId: l.productId,
          quantity: Number(l.quantity),
          status: l.status,
          ...(l.batchId !== '' ? { batchId: l.batchId } : {}),
        })),
      });
      const enabledCount = Array.isArray(saved?.autoEnabledProductIds) ? saved.autoEnabledProductIds.length : 0;
      showAlert(
        enabledCount
          ? t(enabledCount === 1 ? 'inventory.transfers.savedOne' : 'inventory.transfers.savedMany', { n: enabledCount })
          : t('inventory.transfers.saved'),
        'success',
      );
      if (inModal) onSaved?.();
      else navigate(INVENTORY_ROUTES.transfers);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.transfers.saveError')), 'error');
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const locationsSection = (
    <Section plain={inModal} title={t('inventory.transfers.sectionWhere')} description={t('inventory.transfers.whereHelp')}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('inventory.common.origin')}
        </Typography>
        <LocationPicker offices={offices} value={origin} onChange={setOrigin} disabled={!canTransfer} size="medium" />
        <Typography variant="subtitle2" color="text.secondary">
          {t('inventory.transfers.destinationSameOffice')}
        </Typography>
        <LocationPicker
          offices={offices}
          value={destination}
          onChange={(v) => {
            setDestination(v);
            setErrors((e) => ({ ...e, location: undefined }));
          }}
          lockedOfficeId={origin.officeId}
          disabled={!canTransfer || !origin.officeId}
          size="medium"
        />
        {errors.location && (
          <Typography variant="caption" color="error" role="alert" sx={{ mt: -1 }}>
            {errors.location}
          </Typography>
        )}
        <FieldGrid>
          <DateField fullWidth label={t('common.date')} value={date || null} onChange={(v) => setDate(v ?? '')} disabled={!canTransfer} />
        </FieldGrid>
      </Box>
    </Section>
  );

  const productsSection = (
    <Section
      plain={inModal}
      title={t('inventory.transfers.productsToTransfer')}
      description={!isLocationComplete(origin) ? t('inventory.transfers.chooseOriginFirst') : products.length === 0 ? t('inventory.transfers.noStockOrigin') : undefined}
    >
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {lines.map((line, index) => {
          const row = line.productId !== '' ? stockById.get(Number(line.productId)) : undefined;
          const maxQty = row ? (line.status === 'DISPONIBLE' ? row.available : row.notLendable) : 0;
          const lineBatches = batches.filter((b) => b.productId === line.productId && b.status === line.status && b.quantity > 0);
          return (
            <Paper
              key={line.key}
              variant="outlined"
              sx={{
                p: 1.5,
                display: 'grid',
                gap: 1.5,
                alignItems: 'start',
                bgcolor: 'action.hover',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'minmax(0, 3fr) minmax(0, 1.4fr) minmax(0, 2fr) minmax(0, 1.2fr) auto',
                },
              }}
            >
              <TextField
                select
                fullWidth
                size="small"
                label={t('inventory.common.productN', { n: index + 1 })}
                value={line.productId === '' ? '' : String(line.productId)}
                onChange={(e) => updateLine(line.key, { productId: Number(e.target.value) || '', batchId: '' })}
                disabled={!canTransfer || products.length === 0}
                sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
              >
                {products.map((p) => (
                  <MenuItem key={p.productId} value={String(p.productId)}>
                    {p.title} · {t('inventory.transfers.availableShort', { n: p.available })}
                    {p.notLendable ? ` · ${t('inventory.transfers.notLendableShort', { n: p.notLendable })}` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label={t('common.status')}
                value={line.status}
                onChange={(e) => updateLine(line.key, { status: e.target.value as StockStatus, batchId: '' })}
                disabled={!canTransfer}
              >
                {(Object.keys(STOCK_STATUS_LABEL) as StockStatus[]).map((st) => (
                  <MenuItem key={st} value={st}>
                    {STOCK_STATUS_LABEL[st]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label={t('inventory.common.batch')}
                value={line.batchId === '' ? '' : String(line.batchId)}
                onChange={(e) => updateLine(line.key, { batchId: Number(e.target.value) || '' })}
                disabled={!canTransfer || !line.productId}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">{t('inventory.transfers.autoBatch')}</MenuItem>
                {lineBatches.map((b) => (
                  <MenuItem key={b.batchId} value={String(b.batchId)}>
                    {b.lotCode || t('inventory.common.batchFallback', { id: b.batchId })}
                    {b.expirationDate ? ` · ${t('inventory.transfers.expiresOn', { date: formatDate(b.expirationDate) })}` : ''} · {b.quantity}
                  </MenuItem>
                ))}
              </TextField>
              <IntField
                size="small"
                fullWidth
                label={t('inventory.common.quantity')}
                helperText={row ? t('inventory.transfers.maxQty', { n: maxQty }) : undefined}
                error={row ? Number(line.quantity) > maxQty : false}
                value={line.quantity}
                onChange={(v) => updateLine(line.key, { quantity: v })}
                disabled={!canTransfer}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 40 }}>
                <ActionIcon
                  label={t('inventory.common.removeLine')}
                  icon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                  color="error"
                  disabled={!canTransfer || lines.length <= 1}
                  onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== line.key) : prev))}
                />
              </Box>
            </Paper>
          );
        })}
        {errors.lines && (
          <Typography variant="caption" color="error" role="alert">
            {errors.lines}
          </Typography>
        )}
        <Box>
          <Button
            startIcon={<AddOutlinedIcon />}
            onClick={() => setLines((prev) => [...prev, emptyLine(Math.max(...prev.map((l) => l.key)) + 1)])}
            disabled={!canTransfer}
          >
            {t('inventory.common.addProduct')}
          </Button>
        </Box>
        <TextField fullWidth multiline minRows={2} label={t('inventory.common.observation')} value={observation} onChange={(e) => setObservation(e.target.value)} disabled={!canTransfer} />
      </Box>
    </Section>
  );

  if (inModal) {
    return (
      <>
        <StandardModalBody>
          {locationsSection}
          <Divider sx={{ my: 3 }} />
          {productsSection}
        </StandardModalBody>
        <StandardModalFooter>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <SaveButton allowed={canTransfer} deniedTooltip={t('inventory.transfers.noPermission')} loading={saving} onClick={save}>
            {t('inventory.transfers.transfer')}
          </SaveButton>
        </StandardModalFooter>
      </>
    );
  }

  return (
    <PageBody>
      <PageHeader
        title={t('inventory.transfers.newTransfer')}
        breadcrumbs={[{ label: t('inventory.transfers.title'), onClick: () => navigate(INVENTORY_ROUTES.transfers) }, { label: t('inventory.transfers.newTransfer') }]}
      />
      <Box sx={{ display: 'grid', gap: 2, maxWidth: 1200, width: '100%' }}>
        {locationsSection}
        {productsSection}
        <FormActions
          allowed={canTransfer}
          deniedTooltip={t('inventory.transfers.noPermission')}
          onSave={save}
          cancelTo={INVENTORY_ROUTES.transfers}
          saveLabel={t('inventory.transfers.transfer')}
          loading={saving}
        />
      </Box>
    </PageBody>
  );
};

export default StockTransferNew;
